<script setup lang="ts">
// Central's relay for casters (TURN): whether it works, found out by this
// browser asking it for a relay with fresh credentials (the ports, coturn
// and the shared secret at once), and on Google Cloud a superadmin's button
// that opens its ports with their Google sign-in, as provision.sh's
// "<vm>-turn" rule does. Google sends the browser back here with
// ?turn=open, and the opening goes on by itself.
import { onMounted, ref } from 'vue'
import { central, type TURNInfo } from '../../api'
import { useNode } from '../../store'

const node = useNode()
const info = ref<TURNInfo | null>(null)
const state = ref<'checking' | 'works' | 'fails' | 'off'>('checking')
const busy = ref(false)
const note = ref('')

// relayWorks asks the TURN server for a relay candidate, 5 s at most
async function relayWorks(i: TURNInfo): Promise<boolean> {
    const pc = new RTCPeerConnection({ iceServers: i.ice_servers, iceTransportPolicy: 'relay' })
    try {
        pc.createDataChannel('check')
        await pc.setLocalDescription(await pc.createOffer())
        return await new Promise<boolean>((ok) => {
            const t = setTimeout(() => ok(false), 5000)
            pc.onicecandidate = (e) => {
                if (e.candidate?.type === 'relay' || e.candidate?.candidate.includes(' typ relay')) {
                    clearTimeout(t)
                    ok(true)
                } else if (!e.candidate) {
                    clearTimeout(t)
                    ok(false)
                }
            }
        })
    } finally {
        pc.close()
    }
}

async function check() {
    state.value = 'checking'
    try {
        info.value = await central.turn()
    } catch {
        info.value = null
        state.value = 'off'
        return
    }
    if (!info.value.configured) {
        state.value = 'off'
        return
    }
    state.value = (await relayWorks(info.value)) ? 'works' : 'fails'
}

async function open() {
    busy.value = true
    note.value = ''
    try {
        if (!info.value?.signed_in) {
            // Google's sign-in first; it comes back here with ?turn=open
            const back = location.pathname + location.search.replace(/[?&]turn=open/, '')
            location.href = (await central.turnConnect(back)).url
            return
        }
        const r = await central.turnFirewall()
        note.value = r.changed ? `opened (${r.rule}); checking again…` : `${r.rule} was already open`
        await new Promise((ok) => setTimeout(ok, 3000)) // a new rule takes a moment
        await check()
        if (state.value === 'fails')
            note.value = `the firewall is open (${r.rule}), but TURN still does not answer: is coturn running on central's VM (docker compose ps)?`
    } catch (e) {
        note.value = e instanceof Error ? e.message : String(e)
    } finally {
        busy.value = false
    }
}

onMounted(async () => {
    await check()
    const q = new URLSearchParams(location.search)
    if (q.get('turn') === 'open') {
        q.delete('turn')
        history.replaceState(null, '', location.pathname + (q.size ? '?' + q.toString() : ''))
        if (state.value !== 'works' && info.value?.can_open) await open()
        else if (state.value === 'works') node.notify("Central's relay works.")
    }
})
</script>

<template>
    <div class="mt-1.5 text-[11px]" data-test="turn-check">
        <span class="text-fg-2">central's relay (TURN): </span>
        <span v-if="state === 'checking'" class="text-fg-2">checking…</span>
        <span v-else-if="state === 'works'" class="text-ok">works ✓</span>
        <span v-else-if="state === 'off'" class="text-fg-2"
            >not set up: casters connect straight to the node's port only</span
        >
        <template v-else>
            <span class="text-bad">does not answer</span>
            <span class="text-fg-2">
                · casters on the automatic route connect straight to the node's port; "always through central" cannot
                connect</span
            >
            <button
                v-if="info?.can_open"
                class="ml-1 btn-ghost px-1 py-0 text-[11px]"
                :disabled="busy"
                :title="`adds the firewall rule ${info.rule} in ${info.vm?.project} (UDP and TCP 3478, UDP 49160-49999), with your Google sign-in`"
                @click="open"
            >
                {{ busy ? 'opening…' : info.signed_in ? 'Open its ports' : 'Open its ports (sign in with Google)' }}
            </button>
            <span v-else-if="info?.vm" class="text-fg-2">
                · a superadmin can open its ports here once Google sign-in is set up (Cloud nodes)</span
            >
            <span v-else class="text-fg-2"> · open UDP and TCP 3478 and UDP 49160-49999 to central</span>
            <button class="ml-1 btn-ghost px-1 py-0 text-[11px]" :disabled="busy" @click="check">check again</button>
        </template>
        <div v-if="note" class="mt-0.5 text-fg-2">{{ note }}</div>
    </div>
</template>
